// net_selector.js

let netData = [];

document.addEventListener("DOMContentLoaded", async () => {
  await loadData();
  populateFixtureTypes();
  attachInputLogic();
});

// --- UTILS START ---

function getFixtureTypes(data) {
  const fixtureSet = new Set();

  data.forEach((row) => {
    if (row["Suitable For"]) {
      row["Suitable For"].split(",").forEach((type) => {
        fixtureSet.add(type.trim());
      });
    }
  });

  // Replace this line:
  // return Array.from(fixtureSet).sort();

  // With this:
  return Array.from(fixtureSet)
    .filter((t) => t !== "CCTV Cameras") // Exclude CCTV
    .sort((a, b) => {
      if (a === "Others") return 1; // Push 'Others' to bottom
      if (b === "Others") return -1;
      return a.localeCompare(b);
    });
}

function labelFit(score) {
  if (score <= 40) return "Ideal Fit";
  if (score <= 80) return "Snug Fit";
  if (score <= 120) return "Loose Fit";
  return "Oversized Fit";
}

function get3DPermutations(height, width, depth) {
  return [
    [height, width, depth],
    [height, depth, width],
    [width, height, depth],
    [width, depth, height],
    [depth, height, width],
    [depth, width, height],
  ];
}

function findBestFittingNets(
  data,
  height,
  width,
  depth,
  weight,
  selectedFixture,
  topN = 3
) {
  const isCylindrical = depth === null || depth === 0;

  const filtered = data.filter((row) => {
    const types = row["Suitable For"]
      ? row["Suitable For"].split(",").map((x) => x.trim())
      : [];
    return (
      types.includes(selectedFixture) && parseFloat(row["SWL (kgs)"]) >= weight
    );
  });

  if (filtered.length === 0) return [];

  let bestMatches = [];

  if (isCylindrical) {
    [
      [height, width],
      [width, height],
    ].forEach(([h, w]) => {
      const candidates = filtered
        .filter(
          (row) =>
            parseFloat(row["Buffered Height (A)"]) >= h &&
            parseFloat(row["Buffered Width/Diameter (B)"]) >= w
        )
        .map((row) => {
          const score =
            Math.abs(parseFloat(row["Max Height (A)"]) - h) +
            Math.abs(parseFloat(row["Max Width/Diameter (B)"]) - w);
          return { ...row, "Fit Score": score };
        });
      bestMatches.push(...candidates);
    });
  } else {
    get3DPermutations(height, width, depth).forEach(([h, w, d]) => {
      const candidates = filtered
        .filter(
          (row) =>
            parseFloat(row["Buffered Height (A)"]) >= h &&
            parseFloat(row["Buffered Width/Diameter (B)"]) >= w &&
            parseFloat(row["Buffered Depth (C)"]) >= d
        )
        .map((row) => {
          const score =
            Math.abs(parseFloat(row["Max Height (A)"]) - h) +
            Math.abs(parseFloat(row["Max Width/Diameter (B)"]) - w) +
            Math.abs(parseFloat(row["Max Depth (C)"]) - d);
          return { ...row, "Fit Score": score };
        });
      bestMatches.push(...candidates);
    });
  }

  if (bestMatches.length === 0) return [];

  const seen = new Set();
  const uniqueMatches = bestMatches.filter((item) => {
    const sku = item["Variant SKU"];
    if (seen.has(sku)) return false;
    seen.add(sku);
    return true;
  });

  uniqueMatches.sort((a, b) => a["Fit Score"] - b["Fit Score"]);

  return uniqueMatches.slice(0, topN).map((item) => ({
    ...item,
    "Fit Category": labelFit(item["Fit Score"]),
  }));
}

// --- UTILS END ---

async function loadData() {
  try {
    const response = await fetch(window.NET_SELECTOR_DATA_URL);
    netData = await response.json();
  } catch (err) {
    console.error("Failed to load nets_data.json:", err);
    displayError("Unable to load product data. Please try again later.");
  }
}

function populateFixtureTypes() {
  const fixtureSelect = document.getElementById("fixtureType");
  const types = getFixtureTypes(netData);
  types.forEach((type) => {
    const option = document.createElement("option");
    option.value = type;
    option.textContent = type;
    fixtureSelect.appendChild(option);
  });

  fixtureSelect.addEventListener("change", () => {
    const isCCTV = fixtureSelect.value === "CCTV Cameras";
    document.getElementById("cctvGroup").style.display = isCCTV
      ? "block"
      : "none";
    document.getElementById("depthGroup").style.display = isCCTV
      ? "none"
      : "block";
  });
}

function attachInputLogic() {
  const findBtn = document.querySelector("#dropsafe-net-selector button");
  findBtn.addEventListener("click", findNets);
}

function findNets() {
  const fixtureType = document.getElementById("fixtureType").value;
  const height = parseFloat(document.getElementById("height").value);
  const width = parseFloat(document.getElementById("width").value);
  const depthInput = document.getElementById("depth").value;
  const depth = depthInput.trim() === "" ? null : parseFloat(depthInput);
  const weight = parseFloat(document.getElementById("weight").value);
  const topN = 3;
  const isCCTV = fixtureType === "CCTV Cameras";

  const cUnobstructedInput = document.getElementById("cUnobstructed");
  const cUnobstructed = isCCTV ? parseFloat(cUnobstructedInput.value) : null;

  if (isNaN(height) || isNaN(width) || isNaN(weight)) {
    displayError("Please fill in all required fields (height, width, weight).");
    return;
  }

  let results = findBestFittingNets(
    netData,
    height,
    width,
    depth,
    weight,
    fixtureType,
    topN
  );

  if (isCCTV && cUnobstructed !== null) {
    results = results.filter(
      (net) => parseFloat(net["Buffered Depth (C)"]) >= cUnobstructed
    );
  }

  displayResults(results);
}

function displayResults(results) {
  const resultsDiv = document.getElementById("netResults");
  resultsDiv.innerHTML = "";

  if (!results || results.length === 0) {
    displayError(
      "❌ No suitable nets found for the given dimensions and weight."
    );
    return;
  }

  const best = results[0];
  const alternatives = results.slice(1);

  function createTable(nets, title) {
    const table = document.createElement("table");
    table.style.width = "100%";
    table.style.tableLayout = "fixed";
    table.style.marginTop = "1rem";
    table.style.color = "white";
    table.style.borderCollapse = "collapse";

    const headers = [
      { key: "Title", label: "Title", width: "40%" },
      { key: "Variant SKU", label: "Part No.", width: "15%" },
      { key: "SWL (kgs)", label: "SWL (kg)", width: "15%" },
      { key: "Fit Category", label: "Fit", width: "15%" },
      { key: "URL", label: "Link", width: "15%" },
    ];

    const thead = document.createElement("thead");
    const headRow = document.createElement("tr");
    headers.forEach((h) => {
      const th = document.createElement("th");
      th.textContent = h.label;
      th.style.padding = "8px";
      th.style.textAlign = "left";
      if (h.width) th.style.width = h.width;
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    nets.forEach((row) => {
      const tr = document.createElement("tr");
      headers.forEach((h) => {
        const td = document.createElement("td");
        if (h.key === "URL") {
          td.innerHTML = row["URL"]
            ? `<a href="${row["URL"]}" target="_blank" class="net-result-button">View Product</a>`
            : "";
        } else {
          td.textContent = row[h.key] ?? "";
        }
        td.style.padding = "8px";
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);

    const section = document.createElement("div");
    const titleEl = document.createElement("h3");
    titleEl.textContent = title;
    titleEl.style.marginTop = "2rem";
    section.appendChild(titleEl);
    section.appendChild(table);
    return section;
  }

  resultsDiv.appendChild(createTable([best], "🔹 Recommended Net"));
  if (alternatives.length > 0) {
    resultsDiv.appendChild(createTable(alternatives, "🔸 Other Suitable Nets"));
  }

  // Attach print handler AFTER tables are rendered
  const exportBtn = document.getElementById("exportPdfBtn");
  if (exportBtn) {
    exportBtn.addEventListener("click", () => {
      window.print();
    });
  }

  function displayError(msg) {
    const resultsDiv = document.getElementById("netResults");
    resultsDiv.innerHTML = `<p style="color: red;">${msg}</p>`;
  }
}
